// Fixtures — the matches the dashboard is scoped to.
//
// Today these are the 5 hardcoded options in app-web's index.html (all
// England / Premier League). M2 serves them from `GET /fixtures` so the
// frontend selector stops hardcoding them, and every widget endpoint keys its
// cache on a fixture's stable `id` (`pk = FIXTURE#{id}`).
//
// The `id` is a slug derived from the label, so the frontend, the API, and the
// seeder all agree on it without a lookup table. When real fixtures arrive
// (later milestones) `id` becomes the upstream fixture id; nothing else about
// the contract changes.

export interface Fixture {
  /**
   * The fixture's wire id + `FIXTURE#{id}` cache key. From app-api it's the
   * StatsBomb match id as a string (e.g. "3999639") — unique across every league
   * and season, and the same key the model server (Thorpe) joins on (BenBet W1).
   * The static dummy set below still uses label slugs.
   */
  id: string;
  /** Display label, e.g. "Arsenal v Everton". */
  label: string;
  country: string;
  league: string;
  /** Home team, full canonical name. */
  home: string;
  /** Away team, full canonical name. */
  away: string;

  // ── Our data-server IDs (WS-C) ──────────────────────────────────────────
  // Embedded by app-api's fixtures-provider from GET /competitions/{id}/fixtures
  // so per-team / referee / pair widget builders can call the data server by ID
  // (no fragile name-matching). Optional because the frontend never needs them
  // — the wire id stays the slug (WS-C decision) — and the static dummy set
  // below has none. When app-api serves a fixture, all of these are present.
  /** Our fixture (StatsBomb match) id. */
  fixtureId?: number;
  /** Our competition id (PL = 2). */
  competitionId?: number;
  /** Our season id (2025/26 = 318). */
  seasonId?: number;
  /** Home team's data-server id. */
  homeTeamId?: number;
  /** Away team's data-server id. */
  awayTeamId?: number;
  /** Referee's data-server id; null for a not-yet-played fixture. */
  refereeId?: number | null;

  // ── Selector metadata (BenBet W1) ───────────────────────────────────────
  // Lets the selector group by season and default to the next upcoming match.
  /** Season label, e.g. "2026/27". */
  season?: string;
  /** Match date, ISO "YYYY-MM-DD". */
  date?: string;
  /** Kick-off time as the data server reports it, e.g. "15:00:00.000". */
  kickOff?: string;
  /** True once the match has a result. */
  played?: boolean;
}

/**
 * One league-season the app serves (BenBet W1): what the Country / League /
 * Season selectors offer, from `GET /competitions`. IDs are StatsBomb's.
 */
export interface CompetitionSeason {
  competitionId: number;
  seasonId: number;
  country: string;
  league: string;
  /** Season label, e.g. "2026/27" — also the League Table's season key. */
  season: string;
  /** In-progress season: its data changes daily, so app-api refreshes it. */
  live: boolean;
}

/**
 * Slugify a fixture label into its `id`. Lowercase, non-alphanumeric runs → a
 * single hyphen, trimmed. "Manchester United v Newcastle" →
 * "manchester-united-v-newcastle". Pure — reused by the frontend + seeder.
 */
export function fixtureId(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Split a "Home v Away" label into its two team names. */
function teamsOf(label: string): { home: string; away: string } {
  const [home = "", away = ""] = label.split(" v ").map((s) => s.trim());
  return { home, away };
}

function fixture(label: string, country: string, league: string): Fixture {
  const { home, away } = teamsOf(label);
  return { id: fixtureId(label), label, country, league, home, away };
}

// The dummy fixture set — mirrors app-web/index.html's #global-fixture-select.
// All England / Premier League for now.
export const FIXTURES: readonly Fixture[] = [
  fixture("Arsenal v Everton", "England", "Premier League"),
  fixture("Liverpool v Manchester City", "England", "Premier League"),
  fixture("Chelsea v Tottenham", "England", "Premier League"),
  fixture("Manchester United v Newcastle", "England", "Premier League"),
  fixture("Aston Villa v Brighton", "England", "Premier League"),
];

export const DEFAULT_FIXTURE_ID = fixtureId("Arsenal v Everton");

/** Look up a fixture by its slug id. `undefined` if unknown. */
export function fixtureById(id: string): Fixture | undefined {
  return FIXTURES.find((f) => f.id === id);
}
